import React, { useState } from 'react';
import { Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Tooltip, Typography, Menu, MenuItem, Divider, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button } from '@mui/material';
import { Dashboard, Edit, Assignment, AttachMoney, People, SwapHoriz, Campaign, Category, Assessment, ContactMail, Description, FolderOpen, Insights, Person, Logout, Lock, MenuOpen, Menu as MenuIcon } from '@mui/icons-material';
import { DashboardOutlined, EditOutlined, AssignmentOutlined, AttachMoneyOutlined, PeopleOutlined, SwapHorizOutlined, AssessmentOutlined, ContactMailOutlined, DescriptionOutlined, FolderOpenOutlined, InsightsOutlined } from '@mui/icons-material';
import { newLogo, smallInfluencerLogo, sideSmallLogo } from '../assets/images';
import { toast } from 'react-toastify';
import { changePassword } from '../services/api';

function Sidebar({ activeMenu, setActiveMenu, user, isCollapsed, toggleSidebar, onLogout }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordChanging, setPasswordChanging] = useState(false);

  const handleChangePassword = async () => {
    if (!passwordData.currentPassword) { toast.error('Please enter your current password'); return; }
    if (!passwordData.newPassword || passwordData.newPassword.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    if (passwordData.newPassword !== passwordData.confirmPassword) { toast.error('New passwords do not match'); return; }
    setPasswordChanging(true);
    try {
      await changePassword({ currentPassword: passwordData.currentPassword, newPassword: passwordData.newPassword });
      toast.success('Password changed successfully');
      setPasswordModalOpen(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to change password');
    } finally { setPasswordChanging(false); }
  };
  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: <DashboardOutlined /> },
    { id: 'onboarding', label: 'Onboarding Forms', icon: <EditOutlined /> },
    { id: 'existing', label: user?.role === 'onboarder' ? 'Assigned Influencers' : 'Influencers Assign', icon: <PeopleOutlined /> },
    { id: 'barter', label: 'Barter List', icon: <SwapHorizOutlined /> },
    { id: 'negotiation', label: 'Negotiation Cost', icon: <AttachMoneyOutlined /> },
    { id: 'report', label: 'Report', icon: <AssessmentOutlined /> },
    { id: 'contacts', label: 'Identified Influencers', icon: <ContactMailOutlined /> },
    { id: 'filemanager', label: 'File Manager', icon: <FolderOpenOutlined /> },
    { id: 'iydata', label: 'I & Y Data', icon: <InsightsOutlined /> },
    { id: 'marketing', label: 'Inbox', icon: <Campaign /> },
    { id: 'masters', label: 'Masters', icon: <Category /> }
  ].filter(item => {

    if(user?.role === 'marketing'){
      return item.id === 'marketing' || item.id === 'masters' || item.id === 'dashboard';
    }
    if ( user?.role === 'brand_team' || user?.role === 'dm_team' || user?.role === 'imt_team' || user?.role === 'logistics') {
      return item.id === 'marketing' || item.id === 'dashboard';
    }
    if (item.id === 'existing' && user?.role === 'super_admin') {
      return false;
    }
    if (item.id === 'report' && user?.role !== 'admin' && user?.role !== 'super_admin') {
      return false;
    }
    if (item.id === 'contacts' && user?.role !== 'admin') {
      return false;
    }
    if (item.id === 'filemanager' && user?.role !== 'admin') {
      return false;
    }
    if ((item.id === 'marketing' || item.id === 'masters') && user?.role !== 'marketing') {
      return false;
    }
    return true;
  });

  return (
    <Box
      sx={{
        width: isCollapsed ? 70 : 210,
        height: '100vh',
        bgcolor: '#1a1d29',
        position: 'fixed',
        left: 0,
        top: 0,
        zIndex: 1000,
        transition: 'width 0.3s ease',
        borderRight: 'none',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box sx={{ py: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 80, px: isCollapsed ? 0 : 2 }}>
        {isCollapsed ? (
          <Box
            component="img"
            src={sideSmallLogo}
            alt="Logo"
            sx={{ height: 30, width: 30, borderRadius: '50%', objectFit: 'contain' }}
          />
        ) : (
          <Box
            component="img"
            src={newLogo}
            alt="Logo"
            sx={{ height: "50px", width: "140px", objectFit: 'contain' }}
          />
        )}
      </Box>

      <List sx={{ p: 0, px: 1, flex: 1, overflow: 'auto' }}>
        {menuItems.map(item => (
          <ListItem key={item.id} disablePadding sx={{ my: 0.5 }}>
            <Tooltip title={isCollapsed ? item.label : ''} placement="right" arrow>
              <ListItemButton
                selected={activeMenu === item.id}
                onClick={() => setActiveMenu(item.id)}
                sx={{
                  color: '#fff',
                  minHeight: '40px',
                  maxHeight: '40px',
                  borderRadius: '8px',
                  transition: 'all 0.3s ease',
                  my: 0.5,
                  px: 1.5,
                  '&:hover': {
                    bgcolor: '#252836',
                    color: '#fff'
                  },
                  '&.Mui-selected': {
                    bgcolor: '#019383',
                    color: '#fff',
                    '& .MuiListItemIcon-root': {
                      color: '#fff',
                    },
                    '&:hover': {
                      bgcolor: '#019383'
                    }
                  }
                }}
              >
                <ListItemIcon sx={{ color: 'white', minWidth: 30,padding:'5px' }}>
                  {item.icon}
                </ListItemIcon>
                {!isCollapsed && <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '13px' }} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>

      {/* User section at bottom */}
      <Box sx={{ borderTop: '1px solid #2a2d39', p: 1.5 }}>
        <Box
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer',
            p: 1, borderRadius: '8px', '&:hover': { bgcolor: '#252836' }
          }}
        >
          <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#2a2d39', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Person sx={{ color: '#fff', fontSize: 20 }} />
          </Box>
          {!isCollapsed && (
            <>
              <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <Typography sx={{ color: '#fff', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>{user?.username}</Typography>
                <Typography sx={{ color: '#019383', fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.role}</Typography>
              </Box>
              {/* <Box sx={{ color: '#888', fontSize: 14, display: 'flex' }}>⌃</Box> */}
            </>
          )}
        </Box>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="body2" fontWeight={600}>{user?.username}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>{user?.role}</Typography>
          </Box>
          <Divider />
          <MenuItem onClick={() => { setAnchorEl(null); setPasswordModalOpen(true); }}>
            <Lock sx={{ mr: 1, fontSize: 18 }} /> Change Password
          </MenuItem>
          <MenuItem onClick={() => { setAnchorEl(null); onLogout(); }} sx={{ color: '#dc3545' }}>
            <Logout sx={{ mr: 1, fontSize: 18 }} /> Logout
          </MenuItem>
        </Menu>
      </Box>

      <Dialog open={passwordModalOpen} onClose={() => setPasswordModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField fullWidth label="Current Password" type="password" value={passwordData.currentPassword} onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })} margin="normal" />
          <TextField fullWidth label="New Password" type="password" value={passwordData.newPassword} onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })} margin="normal" />
          <TextField fullWidth label="Confirm New Password" type="password" value={passwordData.confirmPassword} onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })} margin="normal" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordModalOpen(false)} disabled={passwordChanging}>Cancel</Button>
          <Button onClick={handleChangePassword} variant="contained" disabled={passwordChanging} sx={{ bgcolor: '#019383' }}>
            {passwordChanging ? 'Changing...' : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Sidebar;
